import { FunctionComponent, useCallback, useEffect, useState } from "react";
import {
  Control,
  Controller,
  RegisterOptions,
  UseFormGetValues,
  UseFormSetValue,
  UseFormTrigger,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { MultiSelect } from "primereact/multiselect";
import { classNames } from "primereact/utils";

import { translationKeys } from "../../../../../models/app-translation-keys";
import FormField, { IFormFieldRules } from "../../FormField/FormField";

import "./MultiSelectInputField.scss";

interface IMultiSelectInputFormFieldRules extends IFormFieldRules {
  minLength?: number;
  maxLength?: number;
}

interface IMultiSelectInputProps {
  control: Control<any, any, any>;
  trigger: UseFormTrigger<any>;
  setValue: UseFormSetValue<any>;
  getValues: UseFormGetValues<any>;
  placeholder: string;
  fieldNameTranslationKey: string;
  fieldDescriptionTranslationKey?: string;
  hideLabel?: boolean;
  fieldName: string;
  optionValue?: string;
  options?: any[];
  disabled?: boolean;
  loadOptions?: () => Promise<any[]>;
  rules?: IMultiSelectInputFormFieldRules;
}

type MultiSelectFormFieldRules = Omit<
  RegisterOptions<any, any>,
  "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
>;

const MultiSelectInputField: FunctionComponent<IMultiSelectInputProps> = (props: IMultiSelectInputProps) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<any[]>([]);

  const loadOptions = async () => {
    setIsLoading(true);
    const loadedOptions = props.loadOptions ? await props.loadOptions!() : [];
    setOptions(loadedOptions);
    setIsLoading(false);
  };

  useEffect(() => {
    if (props.options) {
      setOptions(props.options);
    }

    if (props.loadOptions) {
      loadOptions();
    }
  }, []);

  const formFieldRules = (): MultiSelectFormFieldRules => {
    const rules: MultiSelectFormFieldRules = {};
    if (props.rules?.required) {
      rules.required = t(translationKeys.form.error.isRequired, {
        fieldName: t(props.fieldNameTranslationKey),
      });
    }

    return rules;
  };

  const transformValue = (options: any[], value: any[]): string[] => {
    if (!value?.length) {
      return [];
    }
    const matchingOptions = options
      .filter(option => value.includes(option.id))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
    return matchingOptions.length ? matchingOptions.map(option => option.id) : [];
  };

  useEffect(() => {
    props.trigger(props.fieldName);
  }, [props.fieldName]);

  const sortedOptions = useCallback(() => {
    return options.sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [options]);

  return (
    <Controller
      name={props.fieldName}
      control={props.control}
      rules={formFieldRules()}
      render={({ field, fieldState: { error, invalid } }) => (
        <FormField
          fieldName={props.fieldName}
          fieldNameTranslationKey={props.fieldNameTranslationKey}
          fieldDescriptionTranslationKey={props.fieldDescriptionTranslationKey}
          hideLabel={props.hideLabel}
          error={error}
          invalid={invalid}
        >
          <MultiSelect
            id={field.name}
            {...field}
            value={transformValue(options, field.value)}
            onChange={(e: any) => field.onChange(e.value)}
            options={sortedOptions()}
            optionValue={props.optionValue}
            optionLabel="displayName"
            display="chip"
            className={classNames("w-full multiselect-input", {
              "p-invalid": invalid,
            })}
            placeholder={props.placeholder}
            filter
            disabled={props.disabled}
            loading={isLoading || (!!field.value && !options.length)}
            showClear
          />
        </FormField>
      )}
    />
  );
};

export { MultiSelectInputField };
